<template>
  <div
    :id="`chatInput${keyAfter}`"
    :class="{'is-error': isError}"
    :data-qa="dataQa">
    <slot
      name="beforeInput"/>
    <div
      :class="classes"
      data-qa="chat-input">
      <div
        v-if="fileShow"
        class="chat-document"
        :class="{'is-disabled': isError}"
        :data-qa="`${dataQa}-document`"
        @click="$emit('addFile')">
        <sBadge
          class="mx-auto mb-3 mt-auto"
          :content="fileCount"
          data-qa="chat-input-badge"
          dot
          offset-x="4"
          offset-y="5"
          :value="fileCount">
          <sIcon
            :color="text && !isError ? 'var(--black-color)' : 'grey'"
            size="18"
            :spin="loading"
            :type="loading ? 'loading' : 'add-file'"/>
        </sBadge>
      </div>
      <textarea
        ref="textareaChatInput"
        v-model="text"
        class="chat-input"
        :data-qa="`${dataQa}-textarea`"
        :disabled="disabled || loading"
        :placeholder="$t('enterYouMessage')"
        rows="1"
        :style="textareaCalcStyle"
        :tabindex="0"
        @blur="blur"
        @click="el => showPositionMarker(el, 'click')"
        @focus="focus"
        @input="el => showPositionMarker(el, 'input')"
        @keydown="keymonitor"
        @keydown.tab.exact.prevent=""
        @paste="$emit('paste')"/>
      <div
        class="chat-icon"
        :class="{'is-disabled': disableSend}"
        :data-qa="`${dataQa}-send`"
        @click="sendText">
        <sIcon
          class="mx-auto mb-3 mt-auto"
          :color="disableSend ? 'grey' : 'var(--black-color)'"
          size="18"
          :spin="loading"
          :type="loading ? 'loading' : 'send'"/>
      </div>
    </div>
    <div
      class="chat-input-count"
      :data-qa="`${dataQa}-footer`">
      <div>
        <slot
          name="afterInput"/>
      </div>
      <div
        :data-qa="`${dataQa}-count`"
        style="margin: auto 0;">
        <transition
          name="change-length">
          <span
            :key="`length-${text}`"
            :data-qa="`${dataQa}-count-length`">{{ $n((text ? text : '').length) }}</span>
        </transition>
        <span>/4 000</span>
      </div>
    </div>
    <div
      class="chat-input__cursor-marker"
      :class="keyAfter"
      :style="cursorMarkerStyles">
      <slot name="cursorPosition"/>
    </div>
  </div>
</template>
<script>
import Vue from 'vue'
import { i18n } from '../utils/i18n.js'
import emitter from 'toolkit/mixins/emitter'
import Form from 'toolkit/mixins/form.js'
import sBadge from './badge'
import calcTextareaHeight from 'toolkit/utils/calcTextareaHeight'

Vue.use(i18n)
/**
 * Инпут ввода для чата
 * @displayName ChatInput
 */
export default {
  name: 'sChatInput',
  mixins: [emitter, Form],
  i18n: {
    messages: {
      uk: {
        enterYouMessage: 'Введіть ваше повідомлення'
      },
      ru: {
        enterYouMessage: 'Введите ваше сообщение'
      },
      en: {
        enterYouMessage: 'Enter your message'
      },
    }
  },
  data() {
    return {
      marker: {
        left: 0,
        top: 0,
        width: 0,
      },
      focused: false,
      textareaCalcStyle: {},
    }
  },
  methods: {
    keymonitor(event) {
      const key = event.key
      this.$emit('keydown', event)
      if (this.disabledKeys.indexOf(key) >= 0) {

        event.preventDefault()

        const eventName = key.charAt(0).toLowerCase() + key.slice(1)
        this.$emit(eventName)
      }
    },
    blur() {
      this.$emit('on-blur')
      this.focused = false
    },
    focus() {
      this.focused = true
      this.$emit('on-focus')
    },
    escapeHtml(text) {
      var map = {
        '<': '&lt;',
        '>': '&gt;',
      }
      return text.replace(/[<>]/g, function(m) { return map[m] })
    },
    /**
     * Отправляем текст
     */
    sendText() {
      if (!this.loading && !this.disableSend) {
        const text = this.text?.length ? this.text.trim() : this.text
        this.$emit('send', this.escaped ? this.escapeHtml(text) : text)
        this.text = null
        this.resizeTextarea()
      }
    },
    /**
     * Изменяем размер инпута
     */
    resizeTextarea() {
      const textareaProp = calcTextareaHeight(this.$refs.textareaChatInput, 1, 80)
      if (this.maxHeight) {
        textareaProp.style.maxHeight = this.maxHeight
      }
      this.textareaCalcStyle = textareaProp.style
      this.$nextTick(() => {
        this.$emit('resize', this.$refs.textareaChatInput.clientHeight)
      })
    },
    checkMarkerWidth(minWidth) {
      return new Promise((resolve) => {
        const marker = document.getElementsByClassName(`chat-input__cursor-marker ${this.keyAfter}`)[0]
        let maxChildrenWidth = minWidth || 0
        if (marker && marker.childElementCount) {
          marker.childNodes.forEach((childdren) => {
            if (typeof childdren.getBoundingClientRect === 'function') {
              const width = childdren.getBoundingClientRect().width
              if (width > maxChildrenWidth) { maxChildrenWidth = width }
            }
          })
        }
        this.marker.width = maxChildrenWidth
        resolve()
      })
    },
    showPositionMarker(e, event) {
      this. $emit(event, e)
      const { currentTarget: input } = e

      const {
        offsetLeft,
        offsetTop,
        offsetHeight,
        offsetWidth,
        scrollLeft,
        scrollTop,
        selectionEnd,
      } = input

      this.checkMarkerWidth()

      const { lineHeight, paddingRight } = getComputedStyle(input)
      const { x, y } = this.getCursorXY(input, selectionEnd)
      let newLeft = Math.min(
        x - scrollLeft,
        (offsetLeft + offsetWidth) - parseInt(paddingRight, 10)
      )
      let newTop = Math.min(
        y - scrollTop,
        (offsetTop + offsetHeight) - parseInt(lineHeight, 10)
      )
      newTop = newTop + document.getElementById(`chatInput${this.keyAfter}`).getBoundingClientRect().top + this.deltaMarkerVertical
      newLeft = newLeft + document.getElementById(`chatInput${this.keyAfter}`).getBoundingClientRect().left
      this.marker.left = newLeft
      this.marker.top = newTop

      this.$emit('changeCursorPosition', {left: newLeft, top: newTop})
    },
    getCursorXY(input, selectionPoint) {
      const {
        offsetLeft: inputX,
        offsetTop: inputY,
      } = input
      const div = document.createElement('div')
      const copyStyle = getComputedStyle(input)
      for (const prop of copyStyle) {
        div.style[prop] = copyStyle[prop]
      }

      const inputValue = input.value
      const textContent = inputValue.substr(0, selectionPoint)
      div.textContent = textContent
      div.style.height = 'auto'
      const span = document.createElement('span')

      span.textContent = inputValue.substr(selectionPoint) || '.'
      div.appendChild(span)
      document.body.appendChild(div)
      const { offsetLeft: spanX, offsetTop: spanY } = span
      document.body.removeChild(div)

      return {
        x: inputX + spanX,
        y: inputY + spanY,
      }
    }
  },
  computed: {
    text: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('update:value', value)
      }
    },
    disableSend() {
      return (this.acessEmptyText && !this.fileCount) && (this.isError || !this.text || this.disabled)
    },
    cursorMarkerStyles() {
      const result = {
        left: 0,
        top: `${this.marker.top}px`
      }

      result.left = this.marker.left

      if (result.left + this.marker.width > document.body.clientWidth) {
        result.left = document.body.clientWidth - this.marker.width
      } if (result.left < 0) {
        result.left = 10
      }

      result.left = `${result.left}px`

      return result
    },
    classes() {
      return {
        'chat-input-row': true,
        'is-focus': this.focused,
        'is-disabled': this.disabled || this.loading,
      }
    },
    isError() {
      return this.text?.length > 4000
    }
  },
  watch: {
    text(val) {
      this.dispatch('sFormItem', 's.form.change', [val])
      this.$emit('change')
      this.$nextTick(this.resizeTextarea)
    },
  },
  props: {
    value: String,
    /**
     * Блокируем кнопки или символы (используется при отображении тегов, для отключения перехода по строкам)
     */
    disabledKeys: {
      type: Array,
      default: () => []
    },
    /**
     * отключение
     */
    disabled: {
      type: [Boolean, String],
    },
    /**
     * статус загрузки
     */
    loading: {
      type: [Boolean, String],
    },
    /**
     * Доступность загрузки файлов
     */
    fileShow: {
      type: Boolean
    },
    /**
     * Количество файлов
     */
    fileCount: {
      type: [Number, String]
    },
    maxHeight: {
      type: [Number, String]
    },
    keyAfter: {
      type: String,
      default: ''
    },
    deltaMarkerVertical: {
      type: Number,
      default: 0
    },
    /**
     * Можно отправлять только файл без текста
     */
    acessEmptyText: {
      type: Boolean
    },
    escaped: {
      type: Boolean,
      default: true
    },
    dataQa: {
      type: String,
      default: 'chat-input'
    }
  },
  mounted() {
    /* const getPositionTextArea = document.getElementById('contenteditable')
    getPositionTextArea.addEventListener('input', this.showPositionMarker)
    getPositionTextArea.addEventListener('click', this.showPositionMarker) */
  },
  beforeDestroy() {
    /* const getPositionTextArea = document.getElementById('chatInput')
    getPositionTextArea.removeEventListener('input', this.showPositionMarker)
    getPositionTextArea.removeEventListener('click', this.showPositionMarker) */
  },
  components: {
    sBadge
  }
}
</script>
<style scoped lang="scss">
@import 'src/toolkit/style/variables.scss';

.chat-input-row {
  width: 100%;
  display: flex;
  position: relative;
  border-radius: 8px;
  background: var(--white-color);
	transition: all 0.25s ease;
  border: 2px solid var(--color-main-green);
  &.is-focus {
    outline: none;
    border-color: var(--color-border-active)
	}
  &.is-disabled {
    cursor: default;
    opacity: 0.5;
    border-color: var(--v-secondary__green_not_active-base);
  }
}
.chat-input {
  color: var(--text-color);
  resize: none;
  cursor: auto;
  line-height: 1;
  min-height: 46px !important;
  max-height: 50vh;
  outline: none;
  position: relative;
  padding: 15px 20px;
  border-right-width: 0px;
  width: calc(100% - 57px);
  border-color: transparent;
	// transition: all 0.2s ease;
  border-radius: 8px 0 0 8px;
  &__cursor-marker {
    width: 0px;
    height: 0px;
    position: fixed;
  }
  &:none(.is-disabled):focus,
	&:none(.is-disabled):hover {
    outline: none;
    &::placeholder {
      opacity: 0;
    }
	}
  &::placeholder {
    color: var(--color-disabled);
    font-size: $default-font-size;
  }
}
.chat-document,
.chat-icon {
  width: 57px;
  display: flex;
  min-height: 100%;
	transition: all 0.2s ease;
  cursor: pointer;
  &.is-disabled {
    opacity: 0.5;
  }
  &:hover,
  &:focus {
    background: var(--v-main__light_green-base);
  }
}
.chat-document {
  border-radius: 8px 0 0 8px;
  border-right: 1px solid var(--color-main-green);
}
.chat-icon {
  border-radius: 0 8px 8px 0;
  border-left: 1px solid var(--color-main-green);
}
.chat-input-count {
  margin: 5px 57px 0 0;
  color: var(--color-disabled);
  display: flex;
  justify-content: space-between;
}
.chat-file {
  display: flex;
  padding-left: 20px;
  overflow: hidden;
  max-width: calc(100% - 170px);
}
.is-error {
  .chat-input-count {
    color: var(--color-error);
  }
  .chat-input-row {
    border-color: var(--color-error);
  }
}

.change-length-enter-active {
  transition: all 0.25s ease-in-out;
  transition-delay: 0.1s;
  position: relative;
}
.change-length-leave-active {
  transition: all 0.25s ease-in-out;
  position: absolute;
}
.change-length-enter {
  opacity: 0;
  //transform: translateY(15px);
  pointer-events: none;
}
.change-length-leave-to {
  opacity: 0;
  //transform: translateY(-15px);
  pointer-events: none;
}
</style>
